/*
 * UserProfilePage
 * ----------------
 * Fetches authenticated user's profile using APIHelper and displays the form.
 * Handles global AlertDialog and ProgressBarDialog.
 * Progress bar shows during API calls.
 * Alert dialog appears only on actual errors.
 */

import React, { useState, useEffect } from "react";
import UserProfileForm from "../components/UserProfileForm";
import AlertDialog from "../components/common/AlertDialog";
import ProgressBarDialog from "../components/common/ProgressBar";
import APIHelper from "../api/APIHelper";
import envConfig from "../config/envConfig";

const api = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret,
});

export default function UserProfilePage() {
    const [userDetails, setUserDetails] = useState(null);
    const [progressOpen, setProgressOpen] = useState(false);

    const [dialog, setDialog] = useState({
        open: false,
        title: "",
        message: "",
        type: "info",
        onOk: () => { },
    });

    const showDialog = (opts) => {
        setDialog({
            open: true,
            title: opts.title || "Alert",
            message: opts.message || "",
            type: opts.type || "info",
            onOk: opts.onOk || (() => { }),
        });
    };

    const closeDialog = () => {
        setDialog((d) => ({ ...d, open: false }));
    };

    // ------------------- FETCH USER DETAILS -------------------
    useEffect(() => {
        async function fetchUser() {
            try {
                setProgressOpen(true);

                const appId = envConfig.applicationId;
                const url = appId ? `/arcee/user/me/?application_id=${appId}` : "/arcee/user/me/";
                const resp = await api.apiCall(url, { method: "GET" });

                if (!resp.ok) {
                    let errText = "Failed to load profile data.";
                    try {
                        const errJson = await resp.json();
                        errText = JSON.stringify(errJson, null, 2);
                    } catch { }

                    showDialog({
                        title: "Error",
                        type: "error",
                        message: errText,
                    });
                    return;
                }

                const data = await resp.json();
                setUserDetails(data);

            } catch (err) {
                console.error("Profile fetch error:", err);
                showDialog({
                    title: "Error",
                    type: "error",
                    message: err?.detail || "Unable to fetch user profile.",
                });
            } finally {
                setProgressOpen(false);
            }
        }

        fetchUser();
    }, []);

    // Do not render form until data is ready (spinner already shown)
    if (!userDetails) {
        return (
            <>
                <ProgressBarDialog open={progressOpen} text_message="Loading profile..." />

                <AlertDialog
                    open={dialog.open}
                    title={dialog.title}
                    message={dialog.message}
                    type={dialog.type}
                    showCancel={false}
                    onOk={() => {
                        dialog.onOk();
                        closeDialog();
                    }}
                />
            </>
        );
    }

    return (
        <>
            <UserProfileForm
                userDetails={userDetails}
                showDialog={showDialog}
                showProgress={(state) => setProgressOpen(state)}
            />

            {/* ALERT DIALOG */}
            <AlertDialog
                open={dialog.open}
                title={dialog.title}
                message={dialog.message}
                type={dialog.type}
                showCancel={false}
                onOk={() => {
                    dialog.onOk();
                    closeDialog();
                }}
            />

            {/* PROGRESS BAR */}
            <ProgressBarDialog
                open={progressOpen}
                text_message="Processing..."
            />
        </>
    );
}
